/**
 * Nursing Notes — full report.
 *
 * Prints EVERY nursing entry for a given admission (vitals + medications +
 * notes + who recorded it). Independent of the discharge summary — for
 * nursing-station / case-file archival.
 *
 * Route: /print/nursing-notes/:admissionId
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, BRAND } from "@/lib/api";
import PrintHeader from "@/components/PrintHeader";
import LetterheadToggle, { useLetterheadMode } from "@/components/LetterheadToggle";

export default function PrintNursingNotes() {
  const { admissionId } = useParams();
  const [adm, setAdm] = useState(null);
  const [notes, setNotes] = useState([]);
  const [hospital, setHospital] = useState(null);
  const [branch, setBranch] = useState(null);
  const [patient, setPatient] = useState(null);
  const letterhead = useLetterheadMode();

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        setHospital(me.data.hospital);
        setBranch(me.data.branch);
        const { data } = await api.get(`/ipd/admissions/${admissionId}`);
        setAdm(data.admission);
        // Oldest first for chronological reading
        const list = [...(data.nursing_notes || [])].sort(
          (a, b) => (a.created_at || "").localeCompare(b.created_at || "")
        );
        setNotes(list);
        if (data.admission?.patient_id) {
          const p = await api.get(`/patients/${data.admission.patient_id}`);
          setPatient(p.data.patient);
        }
        const auto = new URLSearchParams(window.location.search).get("autoprint");
        if (auto !== "0") setTimeout(() => window.print(), 800);
      } catch (e) {
        console.error("Nursing notes load failed", e);
      }
    })();
  }, [admissionId]);

  if (!adm) return <div className="p-10 text-sm text-ink-muted">Loading nursing notes…</div>;

  const fmtMeds = (n) => {
    if (Array.isArray(n.medications) && n.medications.length > 0) {
      return n.medications
        .map((m) => [m.medicine, m.strength, m.dose, m.route, m.time, m.notes].filter(Boolean).join(" · "))
        .filter(Boolean)
        .join("; ");
    }
    return n.medications_given || "";
  };

  return (
    <>
      <style>{`
        @page { size: A4; margin: 10mm 12mm; }
        body { background: white; }
        @media print {
          .no-print { display: none !important; }
          tr { page-break-inside: avoid; }
          /* Force browsers to keep our colour fills on paper */
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
        /* Colour palette tuned for a nursing-station printout */
        .nn-stripe-even { background-color: #FFF8E1; }   /* warm cream */
        .nn-stripe-odd  { background-color: #E8F4FD; }   /* sky blue */
        .nn-cell-vitals { background-color: #FEE2E2; }   /* soft red — BP/Pulse */
        .nn-cell-vitalsB { background-color: #FED7AA; }  /* peach — Temp */
        .nn-cell-vitalsC { background-color: #BAE6FD; }  /* light cyan — SpO2 */
        .nn-cell-io     { background-color: #DCFCE7; }   /* mint — Intake/Output */
        .nn-cell-meds   { background-color: #FAE8FF; }   /* lavender — Medications */
        .nn-cell-notes  { background-color: #FEF9C3; }   /* soft yellow — Notes */
      `}</style>
      <div className="mx-auto bg-white text-[12px] leading-snug text-ink" style={{ width: "190mm", padding: "0" }} data-testid="print-nursing-notes">
        <LetterheadToggle />
        {!letterhead && (
          <PrintHeader
            compact
            hospital={hospital}
            branch={branch}
            subtitle="Nursing Notes · Inpatient record"
            right={
              <>
                <div>IPD No.: <b className="font-mono">{adm.ipd_no || "—"}</b></div>
                <div>Ward / Bed: <b>{adm.ward || "—"}{adm.bed_no ? ` · ${adm.bed_no}` : ""}</b></div>
                <div>Admitted: <b>{(adm.admitted_at || "").slice(0, 16).replace("T", " ")}</b></div>
              </>
            }
          />
        )}

        {/* Patient identification strip — coloured pill */}
        <div className="mt-4 grid grid-cols-3 gap-3 rounded-md p-3 text-xs" style={{ background: "linear-gradient(90deg,#EFF6FF 0%, #ECFDF5 50%, #FDF2F8 100%)", border: "1px solid #BFDBFE" }}>
          <div><div className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: "#1D4ED8" }}>Patient</div><div className="font-semibold">{adm.patient_name}</div></div>
          <div><div className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: "#1D4ED8" }}>UHID</div><div className="font-mono">{adm.patient_uhid}</div></div>
          <div><div className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: "#1D4ED8" }}>Sex / Age</div><div>{patient?.gender || "—"} · {patient?.age ?? "—"}</div></div>
          <div><div className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: "#047857" }}>Admission type</div><div className="capitalize">{(adm.admission_type || "").replace("_", " ")}</div></div>
          <div><div className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: "#047857" }}>Reason</div><div className="truncate" title={adm.reason}>{adm.reason || "—"}</div></div>
          <div><div className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: "#BE185D" }}>Payer</div><div className="capitalize">{adm.panel_name || adm.payer_category || "—"}</div></div>
        </div>

        {/* Nursing entries */}
        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "#7C3AED" }}>Nursing entries ({notes.length})</div>
            <div className="text-[10px] text-ink-muted">Chronological · oldest first · colour-coded by vitals / I&amp;O / meds</div>
          </div>
          {notes.length === 0 ? (
            <div className="rounded-md border border-dashed border-border bg-secondary/30 p-6 text-center text-ink-muted">
              No nursing notes recorded for this admission yet.
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr className="text-left text-[9px] uppercase tracking-wider text-white" style={{ background: "#1E3A8A" }}>
                  <th className="py-1.5 px-2">When</th>
                  <th className="py-1.5 px-2">By</th>
                  <th className="py-1.5 px-2">BP</th>
                  <th className="py-1.5 px-2">Pulse</th>
                  <th className="py-1.5 px-2">Temp</th>
                  <th className="py-1.5 px-2">SpO2</th>
                  <th className="py-1.5 px-2">Intake</th>
                  <th className="py-1.5 px-2">Output</th>
                  <th className="py-1.5 px-2">Medications</th>
                  <th className="py-1.5 px-2">Notes</th>
                  <th className="py-1.5 px-2 text-center">Sign</th>
                </tr>
              </thead>
              <tbody>
                {notes.map((n, idx) => (
                  <tr key={n.id} className={`align-top text-[11px] ${idx % 2 === 0 ? "nn-stripe-even" : "nn-stripe-odd"}`}>
                    <td className="py-1 px-2 whitespace-nowrap">{(n.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-1 px-2">{n.created_by_name || "—"}</td>
                    <td className="py-1 px-2 nn-cell-vitals font-semibold">{n.bp || "—"}</td>
                    <td className="py-1 px-2 nn-cell-vitals font-semibold">{n.pulse || "—"}</td>
                    <td className="py-1 px-2 nn-cell-vitalsB font-semibold">{n.temperature || "—"}</td>
                    <td className="py-1 px-2 nn-cell-vitalsC font-semibold">{n.spo2 || "—"}</td>
                    <td className="py-1 px-2 nn-cell-io">{n.intake || "—"}</td>
                    <td className="py-1 px-2 nn-cell-io">{n.output || "—"}</td>
                    <td className="py-1 px-2 nn-cell-meds">{fmtMeds(n) || "—"}</td>
                    <td className="py-1 px-2 nn-cell-notes whitespace-pre-line">{n.notes || "—"}</td>
                    <td className="py-1 px-2 text-center text-ink-muted">_______</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {/* Colour-code legend so the reader knows what each colour means */}
          {notes.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-3 text-[10px]">
              <span className="font-semibold uppercase tracking-wider text-ink-muted">Legend:</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded" style={{ background: "#FEE2E2" }}></span> Vitals (BP/Pulse)</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded" style={{ background: "#FED7AA" }}></span> Temperature</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded" style={{ background: "#BAE6FD" }}></span> SpO2</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded" style={{ background: "#DCFCE7" }}></span> Intake / Output</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded" style={{ background: "#FAE8FF" }}></span> Medications</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded" style={{ background: "#FEF9C3" }}></span> Notes</span>
            </div>
          )}
        </div>

        {/* Sign-off — coloured cards */}
        <div className="mt-10 grid grid-cols-2 gap-6 text-[11px]">
          <div className="rounded-md p-3" style={{ background: "#EEF2FF", border: "1px solid #C7D2FE" }}>
            <div className="mb-6 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "#3730A3" }}>Nurse in-charge</div>
            <div className="border-t border-ink/40 pt-1">Signature &amp; stamp</div>
          </div>
          <div className="rounded-md p-3 text-right" style={{ background: "#ECFDF5", border: "1px solid #A7F3D0" }}>
            <div className="mb-6 text-[10px] font-semibold uppercase tracking-wider" style={{ color: "#065F46" }}>Doctor</div>
            <div className="border-t border-ink/40 pt-1">Signature &amp; stamp</div>
          </div>
        </div>

        <div className="mt-6 border-t border-border pt-2 text-[10px] text-ink-muted flex justify-between">
          <div>{hospital?.name || BRAND.name} · {branch?.name || ""} · Nursing Notes Report</div>
          <div>Generated: {new Date().toISOString().slice(0, 16).replace("T", " ")}</div>
        </div>
        <div className="mt-1 text-center text-[9px] text-ink-muted">{BRAND.footer}</div>

        {/* Floating reprint button (screen only) */}
        <div className="no-print fixed bottom-6 right-6">
          <button
            onClick={() => window.print()}
            className="rounded-md bg-brand px-4 py-2 text-white shadow-lg hover:bg-brand-700"
            data-testid="nursing-print-btn"
          >
            Print / Save as PDF
          </button>
        </div>
      </div>
    </>
  );
}
